import tinycolor from "tinycolor2";
/**
 * Calculates a tint (lighter shade) of the provided color. The provided color
 * can be any common format. This is not lighten, as it scales the requested
 * percent according to color luminance, and desaturates (or saturates if
 * `invert`) the color a tiny amount according to color perception theory.
 *
 * @param color Color to lighten
 * @param percent Percent to lighten
 * @param invert If true, it will calculate a darker shade (useful for dark themes)
 * @returns Hex string of color tint
 */
export function tint(color, percent = 5, invert = false) {
    const c = tinycolor(color);
    const l = c.getLuminance();
    const p = percent * (invert ? 0.1 + l : 1.1 - l);
    return (invert ? c.darken(p).saturate(p / 10) : c.lighten(p).desaturate(p / 10)).toHexString();
}
/**
 * Calculates a shade (darker shade) of the provided color. The provided color
 * can be any common format. This is not darken, as it scales the requested
 * percent according to color luminance, and saturates (or desaturates if
 * `invert`) the color a tiny amount according to color perception theory.
 *
 * @param color Color to darken
 * @param percent Percent to darken
 * @param invert If true, it will calculate a lighter shade (useful for dark themes)
 * @returns Hex string of color shade
 */
export function shade(color, percent = 5, invert = false) {
    const c = tinycolor(color);
    const l = c.getLuminance();
    const p = percent * (invert ? 1.1 - l : (0.5 + l) / 2);
    return (invert ? c.lighten(p).desaturate(p / 10) : c.darken(p).saturate(p / 10)).toHexString();
}
/**
 * Calculates a best effort contrast color (readable contrast) for the provided
 * color. Will fall back to black or white if a suitable color is not found.
 *
 * @param color Color to calculate contrast for.
 * @returns Hex string of contrast color
 */
export function contrast(color) {
    const luminance = tinycolor(color).getLuminance();
    if (luminance > 0.35) {
        if (luminance > 0.6) {
            const darkOne = shade(color, 65);
            if (tinycolor(darkOne).getLuminance() > 0 &&
                tinycolor.isReadable(color, darkOne, { size: "large" }))
                return darkOne;
            const darkTwo = shade(color, 75);
            if (tinycolor(darkTwo).getLuminance() > 0 &&
                tinycolor.isReadable(color, darkTwo, { size: "large" }))
                return darkTwo;
        }
        const darkOne = shade(color, 45);
        if (tinycolor.isReadable(color, darkOne, { size: "large" }))
            return darkOne;
        const darkTwo = shade(color, 55);
        if (tinycolor.isReadable(color, darkTwo, { size: "large" }))
            return darkTwo;
        return tinycolor.isReadable(color, "#222222", { size: "large" })
            ? "#222222"
            : "#000000";
    }
    else {
        if (luminance < 0.3) {
            const lightOne = tint(color, 85);
            if (tinycolor.isReadable(color, lightOne, { size: "large" }))
                return lightOne;
        }
        const lightTwo = tint(color, 75);
        if (tinycolor.isReadable(color, lightTwo, { size: "large" }))
            return lightTwo;
        return tinycolor.mostReadable(color, ["#000", "#fff"]).toHexString();
    }
}
/**
 * Convert any color format to hex rgb.
 *
 * @param color Color in any common format
 * @returns Hex string of color
 */
export function hex(color) {
    return tinycolor(color).toHexString();
}
/**
 * Convert any color to an rgb string (not wrapped in rgb(), e.g. "3, 44, 211").
 *
 * @param color Color in any common format
 * @returns Rgb string of color (i.e. "3, 44, 211")
 */
export function rgb(color) {
    const { r, g, b } = tinycolor(color).toRgb();
    return `${r}, ${g}, ${b}`;
}
